/**
 * Trial Status Card Component
 *
 * Displays trial status in the sidebar with progress indicators
 * and upgrade call-to-action.
 */

import { Link } from 'react-router-dom';
import { ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Badge } from '@/components/catalyst/badge';
import { Button } from '@/components/catalyst/button';
import {
  formatDaysRemaining,
  formatChecksRemaining,
  getTrialColorScheme,
  type TrialStatus,
} from '../utils/trialHelpers';

interface TrialStatusCardProps {
  status: TrialStatus;
}

export function TrialStatusCard({ status }: TrialStatusCardProps) {
  if (!status.isOnTrial) {
    return null;
  }

  const colors = getTrialColorScheme(status);
  const checksPercentage = status.checkLimit
    ? Math.round(((status.checksUsed || 0) / status.checkLimit) * 100)
    : 0;

  return (
    <div
      className={`rounded-lg border p-4 ${colors.bg} ${colors.border}`}
      role="region"
      aria-label="Trial status"
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <h3 className={`text-sm font-semibold ${colors.text}`}>Trial Status</h3>
        <Badge color={colors.badge}>Trial</Badge>
      </div>

      {/* Days Remaining */}
      <div className="mb-3 flex items-start gap-2">
        <ClockIcon className={`h-5 w-5 flex-shrink-0 ${colors.text}`} />
        <div className="flex-1">
          <p className={`text-sm font-medium ${colors.text}`}>
            {formatDaysRemaining(status.daysRemaining || 0)}
          </p>
          {status.daysRemaining !== null && status.daysRemaining > 0 && (
            <p className="text-xs text-zinc-600 dark:text-zinc-400">
              Expires {status.expiresAt?.toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {/* Checks Remaining */}
      <div className="mb-3 flex items-start gap-2">
        <CheckCircleIcon className={`h-5 w-5 flex-shrink-0 ${colors.text}`} />
        <div className="flex-1">
          <p className={`text-sm font-medium ${colors.text}`}>
            {formatChecksRemaining(status.checksUsed || 0, status.checkLimit || 25)}
          </p>

          {/* Progress Bar */}
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
            <div
              className={`h-full transition-all duration-300 ${
                status.isCritical
                  ? 'bg-red-500'
                  : status.isApproachingLimit
                    ? 'bg-amber-500'
                    : 'bg-blue-500'
              }`}
              style={{ width: `${checksPercentage}%` }}
              role="progressbar"
              aria-valuenow={checksPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${checksPercentage}% of trial checks used`}
            />
          </div>
        </div>
      </div>

      {/* Warning Message */}
      {status.isCritical && (
        <div className="mb-3 rounded-md bg-red-100 p-2 dark:bg-red-900/20">
          <p className="text-xs font-medium text-red-800 dark:text-red-200">
            {status.checksRemaining === 0 ? 'Trial check limit reached' : 'Trial has expired'}
          </p>
        </div>
      )}

      {/* Upgrade CTA */}
      <Link to="/billing">
        <Button color="blue" className="w-full">
          Upgrade Now
        </Button>
      </Link>
    </div>
  );
}
